import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, LoaderCircle } from "lucide-react";

// Návrat ze Stripe Checkoutu. Polluje stav platby (max ~40 s), po zaplacení
// pošle uživatele zpět do aplikace — /auth/me už vrátí odemčený stav.
export function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const tries = useRef(0);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) {
        // 404/4xx = neznámá session — nemá smysl čekat 40 s
        const st = e.response?.status;
        if (st && st >= 400 && st < 500) { setState("error"); return; }
      }
      if (tries.current >= 20) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="payment-success-page">
      <div className="w-full max-w-[480px] bg-card border border-border rounded-[20px] shadow-xl px-8 py-10 text-center">
        {state === "checking" && (<>
          <LoaderCircle size={40} className="mx-auto mb-4 text-brand animate-spin" />
          <h1 className="text-xl font-extrabold mb-2">Ověřuji platbu…</h1>
          <p className="text-sm text-muted-foreground">Chvilku strpení, potvrzujeme platbu se Stripe.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 size={44} className="mx-auto mb-4 text-success" />
          <h1 className="text-xl font-extrabold mb-2" data-testid="payment-paid-title">Zaplaceno — děkujeme!</h1>
          <p className="text-sm text-muted-foreground mb-6">Tarif je aktivní a přístup odemčený.</p>
          <a href="/" className="inline-flex items-center justify-center h-11 px-6 rounded-[14px] bg-brand text-white font-bold" data-testid="payment-continue">Pokračovat do aplikace</a>
        </>)}
        {state === "timeout" && (<>
          <LoaderCircle size={40} className="mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-xl font-extrabold mb-2">Platba se stále zpracovává</h1>
          <p className="text-sm text-muted-foreground mb-6">Jakmile ji Stripe potvrdí, přístup se odemkne sám. Zkuste za chvíli obnovit stránku.</p>
          <a href="/" className="inline-flex items-center justify-center h-11 px-6 rounded-[14px] bg-card border border-border font-bold">Zpět do aplikace</a>
        </>)}
        {state === "error" && (<>
          <XCircle size={44} className="mx-auto mb-4 text-destructive" />
          <h1 className="text-xl font-extrabold mb-2">Platba neproběhla</h1>
          <p className="text-sm text-muted-foreground mb-6">Nic jsme nestrhli. Zkuste to prosím znovu.</p>
          <a href="/" className="inline-flex items-center justify-center h-11 px-6 rounded-[14px] bg-brand text-white font-bold">Zpět do aplikace</a>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="payment-cancel-page">
      <div className="w-full max-w-[480px] bg-card border border-border rounded-[20px] shadow-xl px-8 py-10 text-center">
        <XCircle size={44} className="mx-auto mb-4 text-muted-foreground" />
        <h1 className="text-xl font-extrabold mb-2">Platba zrušena</h1>
        <p className="text-sm text-muted-foreground mb-6">Nic jsme nestrhli. Tarif můžete zaplatit kdykoli znovu.</p>
        <a href="/" className="inline-flex items-center justify-center h-11 px-6 rounded-[14px] bg-brand text-white font-bold" data-testid="payment-cancel-back">Zpět do aplikace</a>
      </div>
    </div>
  );
}
